// QA-only telemetry for static-geometry virtual rows, active when the Playwright scroll suite
// sets window.__KNOV_QA_GEOMETRY_PROBE__ before the app bundle loads (via addInitScript):
//  - window.__knovVirtualRowMounts: row key → cumulative DOM mount count. A key that stays in
//    the rowset must never re-mount during pure scrolling; the QA remount probe asserts this.
//  - data-virtual-row-spill on each row: px of content overflowing the reserved row height
//    (visible as clipping under the static-row overflow rule). The QA spill probe surfaces
//    estimator gaps even though clipping keeps them from corrupting neighboring rows.

const qaWindow = () => /** @type {any} */ (typeof window !== 'undefined' ? window : null)

export function staticGeometryQaProbeEnabled() {
    try {
        return !!qaWindow()?.__KNOV_QA_GEOMETRY_PROBE__
    } catch {
        return false
    }
}

// React calls inline ref callbacks on every commit; only a brand-new DOM node is a mount.
const observedRowNodes = new WeakSet()

function recordRowMount(rowKey) {
    const w = qaWindow()
    if (!w) return
    const counts = (w.__knovVirtualRowMounts = w.__knovVirtualRowMounts || {})
    counts[rowKey] = (counts[rowKey] || 0) + 1
}

function updateRowSpill(node) {
    if (!node.isConnected) {
        // Rows churn constantly under virtualization; dropping dead nodes here keeps the
        // shared observer from accumulating thousands of stale targets over a long QA run.
        sharedSpillObserver?.unobserve(node)
        return
    }
    const reserved = node.clientHeight
    const content = node.scrollHeight
    node.dataset.virtualRowSpill = String(Math.max(0, content - reserved))
}

// One observer for every probed row (QA-only): per-row observers would pile up across the
// mount/unmount churn of a long scroll suite and skew its timing measurements.
let sharedSpillObserver = null

function resolveSharedSpillObserver() {
    if (sharedSpillObserver || typeof ResizeObserver === 'undefined') return sharedSpillObserver

    sharedSpillObserver = new ResizeObserver(entries => {
        entries.forEach(entry => {
            if (!entry.target.isConnected) {
                sharedSpillObserver?.unobserve(entry.target)
                return
            }
            const row = entry.target.closest?.('[data-virtual-row-spill]') || entry.target
            scheduleRowSpillUpdate(row)
        })
    })
    return sharedSpillObserver
}

function scheduleRowSpillUpdate(node) {
    if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => updateRowSpill(node))
    } else {
        updateRowSpill(node)
    }
}

function attachRowSpillProbe(node) {
    node.dataset.virtualRowSpill = '0'
    scheduleRowSpillUpdate(node)

    const observer = resolveSharedSpillObserver()
    if (!observer) return
    observer.observe(node)
    if (node.firstElementChild) observer.observe(node.firstElementChild)
}

// Compose into a row's ref callback: counts the mount and keeps the spill attribute fresh.
// No-ops entirely outside QA runs.
export function updateStaticRowQaProbe(node, rowKey) {
    if (!node || !staticGeometryQaProbeEnabled()) return
    if (observedRowNodes.has(node)) return

    observedRowNodes.add(node)
    recordRowMount(String(rowKey))
    attachRowSpillProbe(node)
}
